import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { HelpTip } from '../../../../components/HelpTip';
import { Modal } from '../../../../components/Modal';
import { InlineAddTable, InlineRow } from '../../../../components/inline-add-table/InlineAddTable';
import { TdSeamless } from '../../../../components/inline-add-table/cells';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { uid } from '../../../../lib/uid';
import { plantillaApi } from '../plantillaApi';

/**
 * What the grid is and what it commits to, said once — see the twin in `<PlantillaPositionModal>`.
 */
const SLOTS_HELP =
  'One row per slot — one authorized post that one person can hold. Item numbers are ' +
  'permanent: removing a slot later does not free its number for reuse.';

/**
 * Authorize more slots under a post.
 *
 * Its own modal, and its own permission, because a headcount change is a different decision
 * from editing a title — and the consequence is on the form rather than on the tick: the
 * numbers issued here are permanent.
 */
export function AddSlotsModal({
  open,
  positionId,
  positionCode,
  nextNumber,
  onClose,
  onSaved,
}: {
  open: boolean;
  positionId: string;
  positionCode: string;
  /**
   * The number the next slot will take, as the page behind this form knows it.
   *
   * A SEED, not the answer: the profile's own list hides deleted slots unless the reader has
   * asked for them, and the server numbers past deleted items — so this form re-reads the full
   * history and corrects itself. Keeping it means the first paint shows a number rather than a
   * blank while that lands.
   */
  nextNumber: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  /*
    ONE ROW PER SLOT — product call, 09 Oct 2026, replacing a `How many` number box.

    The same grid the create form now uses, and for the same reason: the rows ARE the count,
    and each one shows the number it will be given. The preview is exact here — this form is
    about one known post, so the code and the next sequence are both in hand.
  */
  const [rows, setRows] = useState<{ key: string }[]>(() => [{ key: uid() }]);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const parsed = rows.length;

  // The rows are the count, so the ceiling the server holds is the only thing left to check.
  const countValid = parsed >= 1 && parsed <= 200;
  const canSave = countValid && confirmed && !saving;

  /*
    WHERE THE NUMBERING ACTUALLY RESUMES — deleted items included, which the caller's figure
    may not be. A removed slot keeps its number forever, so a preview counting only live ones
    promises numbers the save will not use, and every row in the grid would be wrong by the
    same amount.
  */
  const [nextFrom, setNextFrom] = useState(nextNumber);

  useEffect(() => {
    const controller = new AbortController();

    plantillaApi
      .slots(positionId, true, controller.signal)
      .then((items) =>
        setNextFrom(items.reduce((highest, i) => Math.max(highest, i.sequenceNumber), 0) + 1),
      )
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === 'AbortError') return;

        // The caller's figure stands. It is the same one the profile behind this form shows.
      });

    return () => controller.abort();
  }, [positionId]);

  const itemNumber = (index: number) =>
    `${positionCode}-${String(nextFrom + index).padStart(3, '0')}`;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      await plantillaApi.addSlots(positionId, parsed);
      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not add the slots.', 0);

      setError(failure);

      // Say which field, show it, and — on a stepped form — go to its step.
      focusValidationError(failure, { idPrefix: 'add-slots' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add slots"
      /* A grid rather than one box: `sm` clipped the item-number column. */
      size="md"
      id="add-slots-modal"
      /* One field, and the form already refuses anything outside 1–200. */
      autogenerate={{
        onGenerate: () =>
          setRows(Array.from({ length: 1 + Math.floor(Math.random() * 5) }, () => ({ key: uid() }))),
        disabled: saving,
      }}
      confirm={{
        id: 'add-slots-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="add-slots-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="add-slots-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <div>
          {/*
            The consequence travels with the label, in the HelpTip and in the DOM `sr-only`
            beside it — the product's own departure from reference §5. It is on the form rather
            than appended to the confirm tick: these numbers are permanent.
          */}
          <div className="flex min-h-5 items-center gap-1.5 pb-1.5">
            <p className="text-sm text-slate-600">Slots</p>
            <HelpTip label={SLOTS_HELP} />
          </div>

          <p className="sr-only">{SLOTS_HELP}</p>

          <InlineAddTable
            headers={['Item number']}
            colWidths={[320]}
            flexColumn={0}
            rowCount={rows.length}
            onAddRow={() => setRows((current) => [...current, { key: uid() }])}
            addDisabled={saving || !countValid}
            addDisabledReason="A post may authorize at most 200 items."
            addButtonId="add-slots-add-row"
          >
            {rows.map((row, index) => (
              <InlineRow
                key={row.key}
                idx={index + 1}
                onRemove={() =>
                  setRows((current) => {
                    const next = current.filter((r) => r.key !== row.key);

                    // Never nothing: this form adds at least one slot, and an empty grid has
                    // no row to press a `+` from.
                    return next.length > 0 ? next : [{ key: uid() }];
                  })
                }
              >
                {/* Read-only: the number is the plantilla's, and this is what it will be. */}
                <TdSeamless>
                  <span className="truncate font-mono text-sm text-slate-600">
                    {itemNumber(index)}
                  </span>
                </TdSeamless>
              </InlineRow>
            ))}
          </InlineAddTable>
        </div>
      </div>
    </Modal>
  );
}
